
import { useRef, useState, useEffect } from 'react';
import KIAImage from '../../components/ui/image/KIAImage';

interface ImgCarouselProps {
  images: {
    src: string;
    alt: string;
    sizes?: string;
    [key: string]: any;
  }[];

  height?: string;

  slideWidth?: number;

  gap?: number;
}

const ImgCarousel: React.FC<ImgCarouselProps> = ({
  images,
  height = '',
  slideWidth = 500,
  gap = 10,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);


  const CLONES = 4;
  const originalLength = images.length;
  const extendedImages = [
    ...images.slice(-CLONES),
    ...images,
    ...images.slice(0, CLONES),
  ];


  const [currentIndex, setCurrentIndex] = useState(CLONES);

  const [transitionEnabled, setTransitionEnabled] = useState(true);


  const moveDistance = slideWidth + gap;


  const centerOffset = (containerWidth - slideWidth) / 2;
  const totalOffset = currentIndex * moveDistance - centerOffset;
  const containerTransform = `translateX(-${totalOffset}px)`;

 
  const handleTransitionEnd = () => {
    if (currentIndex >= originalLength + CLONES) {
      setTransitionEnabled(false);
      setCurrentIndex(CLONES);
    } else if (currentIndex < CLONES) {
      setTransitionEnabled(false);
      setCurrentIndex(originalLength + CLONES - 1);
    }
  };

 
  useEffect(() => {
    if (!transitionEnabled) {
      const timer = setTimeout(() => setTransitionEnabled(true), 50);
      return () => clearTimeout(timer);
    }
  }, [transitionEnabled]);


  const handlePrev = () => setCurrentIndex((i) => i - 1);
  const handleNext = () => setCurrentIndex((i) => i + 1);


  const touchStartX = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {

  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current !== null) {
      const delta = touchStartX.current - e.changedTouches[0].clientX;
      const threshold = 50;
      if (delta > threshold) handleNext();
      if (delta < -threshold) handlePrev();
    }
    touchStartX.current = null;
  };


  const realIndex = (currentIndex - CLONES + originalLength) % originalLength;

  return (
    <div ref={containerRef} className="relative w-full overflow-hidden">
   
      <button
        onClick={handlePrev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10  bg-black bg-opacity-30 text-white rounded-full  p-2 hover:bg-opacity-50"
        aria-label="Previous slide"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 31 39"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M29 37L4 17.4412L29 2" stroke="white" strokeWidth="4" />
        </svg>
      </button>


      <button
        onClick={handleNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black bg-opacity-30 text-white rounded-full p-2 hover:bg-opacity-50"
        aria-label="Next slide"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 31 39"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M2 37L27 17.4412L2 2" stroke="white" strokeWidth="4" />
        </svg>
      </button>

  
      <div
        className="flex"
        style={{
          transform: containerTransform,
          transition: transitionEnabled ? 'transform 0.3s ease' : 'none',
          gap: `${gap}px`,
        }}
        onTransitionEnd={handleTransitionEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {extendedImages.map((imgProps, idx) => (
          <div
            key={idx}
            className="flex-shrink-0 relative"
            style={{
              minWidth: `${slideWidth}px`,
              maxWidth: `${slideWidth}px`,
              height,
            }}
          >
            <KIAImage
              {...imgProps}
              height={height}
              className={
                idx === currentIndex ? 'opacity-100' : 'opacity-50'
              }
              style={{
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              }}
            />
          </div>
        ))}
      </div>

   
      <div className="flex justify-center mt-4">
        {images.map((_, idx) => (
          <div
            key={idx}
            className={`w-3 h-3 rounded-full mx-1 ${
              idx === realIndex ? 'bg-black' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default ImgCarousel;
